/**
 * 返事の読み上げ（ADR-0027、方式 A）。
 *   返事が少しずつ届く → 文に切る → 文ごとに「声を作る場所」（VoiceSource）に頼む → 届いた順ではなく文の順に鳴らす。
 *   最初の文は返事の途中で頼み始めるので、返事が全部できる前に鳴り始める。鳴らしている間に次の文を頼んでおく。
 *
 * 声を作る場所は差し替えられる（原則 4）: 今は Gateway 経由で MacBook（GatewayVoice）。
 * Phase 3 で常駐の計算機、自作の声が育ったらスマホの中で合成する口に替える。ReplyReader と画面は変えない。
 *
 * 届かないとき（MacBook がスリープ・オフライン、未設定）は、その返事の残りをブラウザ標準の声（speechSynthesis）で読み、
 * 声が変わったことを画面に出す（onVoice）。次の返事ではまた Jarvis の声を試す。
 */
import { GATEWAY, store } from "./api.js";
import { type RangedSentence, RangedSentenceCutter } from "./sentences.js";

/** 1 回の要求の文字数の上限（Gateway・中継と同じ。packages/contracts の SPEECH_MAX_CHARS） */
export const SPEECH_MAX_CHARS = 200;
/** 1 回の要求に添える読みの数の上限（packages/contracts の SPEECH_READINGS_MAX と同じ） */
export const SPEECH_READINGS_MAX = 30;
/** 1 回の返事で頼む文の数の上限。長い返事で要求が続きすぎないように（それ以降は読まない） */
export const MAX_SENTENCES_PER_REPLY = 40;
/** 鳴らしている文より先に頼んでおく文の数 */
const LOOKAHEAD = 2;

/** 1 文に添えるもの（ADR-0041） */
export interface SynthExtra {
  /** この文に出てくる語の読み（返事の最初の知らせで届いたもの） */
  readings?: Record<string, string>;
  /** 返事の何文目か（0 から。取り消して読み直したら 0 から数え直す）。0 の要求の終わりが最初の音の計測 */
  seq?: number;
}

/** 声を作る場所 */
export interface VoiceSource {
  readonly name: string;
  /** 1 文を音声にする。作れなければ SpeechUnavailable を投げる */
  synth(
    text: string,
    signal: AbortSignal,
    replyTraceId: string | null,
    extra?: SynthExtra,
  ): Promise<Blob>;
}

/** 契約（packages/contracts の SpeechReadings）に合う読みか: 表記は 1〜20 字、読みはかなだけで 1〜40 字 */
const READING_OK = /^[ぁ-ゖァ-ヺー]{1,40}$/;

/**
 * 読みのうち、この文に出てくる語だけ（中継への要求を小さく保つ）。契約に合わない読みは添えない
 * （1 つでも合わないと要求ごと断られ、その返事の残りが標準の声になるため）
 */
export function readingsFor(
  readings: Record<string, string> | null,
  text: string,
): Record<string, string> | undefined {
  if (!readings) return undefined;
  const hits = Object.entries(readings)
    .filter(
      ([surface, kana]) =>
        surface.length > 0 &&
        surface.length <= 20 &&
        READING_OK.test(kana) &&
        text.includes(surface),
    )
    .slice(0, SPEECH_READINGS_MAX);
  return hits.length > 0 ? Object.fromEntries(hits) : undefined;
}

export class SpeechUnavailable extends Error {
  constructor(
    readonly code: string,
    readonly reason?: string,
  ) {
    super(reason ? `${code}: ${reason}` : code);
    this.name = "SpeechUnavailable";
  }
}

/** 音を出す側（ブラウザでは BrowserOutput。テストでは模擬） */
export interface SpeechOutput {
  playAudio(audio: Blob, signal: AbortSignal): Promise<void>;
  /** 標準の声で読む（声を作る場所に届かないとき） */
  speakFallback(text: string, signal: AbortSignal): Promise<void>;
  /**
   * 今鳴らしている音の、どこまで鳴らしたか（0〜1。<audio> の currentTime / duration）。分からなければ null
   * （標準の声・鳴らしていない）。割り込みの報告の位置に使う（ADR-0042）
   */
  progress?(): number | null;
}

/** どこまで声に出したか（割り込みの報告の voice。ADR-0042 の 2.5）。位置は返事の文の中のコードポイント */
export interface SpokenPosition {
  /** 止まった文（0 から。最後の取り消しの後に読み始めた文から数える。長い文は 200 字ずつに分けた単位） */
  sentence_index: number;
  sentence_start: number;
  sentence_end: number;
  /** その文の何割まで（0〜1000） */
  spoken_permille: number;
}

export type VoiceState =
  | { kind: "jarvis"; source: string }
  | { kind: "fallback"; code: string; reason?: string };

/** 長すぎる文を、読点・空白で 200 文字以下に分ける（無ければ字数で切る） */
export function chunkForSpeech(sentence: string, max = SPEECH_MAX_CHARS): string[] {
  const out: string[] = [];
  let rest = sentence.trim();
  while (rest.length > max) {
    const head = rest.slice(0, max);
    const cut = Math.max(head.lastIndexOf("、"), head.lastIndexOf("，"), head.lastIndexOf(" "));
    const at = cut >= max / 2 ? cut + 1 : max;
    out.push(rest.slice(0, at).trim());
    rest = rest.slice(at).trim();
  }
  if (rest) out.push(rest);
  return out;
}

interface Item {
  text: string;
  /** 返事の文の中の範囲（コードポイント。長い文を分けたときは元の文の範囲） */
  start: number;
  end: number;
  /** 音声。null なら標準の声で読む */
  audio: Promise<Blob | null> | null;
}

/**
 * 1 回の返事を読み上げる。partial（累積テキスト）→ finish（確定文）の順に渡す。stop() でいつでも止まる。
 * 頼むのは 1 本ずつ（中継の合成も 1 本ずつ）、鳴らすのは文の順。
 * 文末まで揃った文から頼むので、読み始めは 1 文目が揃ったとき。retract()（流した文の取り消し: ツールの呼び出しが
 * 始まった・答え直す）で、鳴らしている文・頼んでいる文を止め、次の累積テキストを最初から読み直す（ADR-0041）。
 */
export class ReplyReader {
  replyTraceId: string | null = null;
  /** 返事の最初の知らせで届いた読み（表記 → かな。ADR-0041）。この文に出てくる語だけを中継に添える */
  readings: Record<string, string> | null = null;
  private cutter = new RangedSentenceCutter();
  private items: Item[] = [];
  private readonly ac = new AbortController();
  /** 今の読み（取り消すたびに作り直す）。鳴らす・頼むのはこの signal で止める */
  private gen = new AbortController();
  private genId = 0;
  private playing = 0;
  /** 今の文を鳴らしているところか（声を作るのを待っている間は false） */
  private sounding = false;
  private finished = false;
  private degraded: VoiceState | null = null;
  private announced: VoiceState["kind"] | null = null;
  private wake: (() => void) | null = null;
  private chain: Promise<unknown> = Promise.resolve();
  /** 読み終わる（または止まる）と解決する */
  readonly done: Promise<void>;

  constructor(
    private readonly o: {
      source: VoiceSource | null;
      out: SpeechOutput;
      onVoice?: (s: VoiceState) => void;
      onError?: (e: unknown) => void;
      maxSentences?: number;
    },
  ) {
    if (!o.source) this.degraded = { kind: "fallback", code: "speech_not_configured" };
    this.done = this.loop();
  }

  partial(accumulated: string) {
    if (!this.finished) this.add(this.cutter.push(accumulated));
  }

  finish(finalText: string) {
    if (this.finished) return;
    this.add(this.cutter.finish(finalText));
    this.finished = true;
    this.wake?.();
  }

  stop() {
    this.finished = true;
    this.ac.abort();
    this.gen.abort();
    this.wake?.();
  }

  /** 流した文の取り消し。鳴らしている文・頼んでいる文を止め、次の累積テキストを最初から読み直す */
  retract() {
    if (this.finished) return;
    this.gen.abort();
    this.gen = new AbortController();
    this.genId += 1;
    this.cutter = new RangedSentenceCutter();
    this.items = [];
    this.playing = 0;
    this.chain = Promise.resolve();
    this.wake?.();
  }

  get stopped() {
    return this.ac.signal.aborted;
  }

  /**
   * 今どこまで声に出したか（割り込みの報告。ADR-0042）。まだ 1 文も揃っていない・全部読み終えたときは null。
   * 鳴らす前（声を作っている間）は、その文の 0 割
   */
  position(): SpokenPosition | null {
    if (this.finished && this.playing >= this.items.length) return null;
    const item = this.items[this.playing];
    if (!item) return null;
    const p = this.sounding ? (this.o.out.progress?.() ?? null) : 0;
    const permille = Math.max(0, Math.min(1000, Math.round((p ?? 0) * 1000)));
    return {
      sentence_index: this.playing,
      sentence_start: item.start,
      sentence_end: item.end,
      spoken_permille: permille,
    };
  }

  /** 全部読み終えたか（止めた・読まなかった文があれば false） */
  get completed(): boolean {
    return this.finished && !this.stopped && this.playing >= this.items.length;
  }

  private add(sentences: RangedSentence[]) {
    const max = this.o.maxSentences ?? MAX_SENTENCES_PER_REPLY;
    for (const x of sentences)
      for (const s of chunkForSpeech(x.text)) {
        if (this.items.length >= max) break;
        this.items.push({ text: s, start: x.start, end: x.end, audio: null });
      }
    this.request();
    this.wake?.();
  }

  /** 鳴らしている文から LOOKAHEAD 先までを、順に 1 本ずつ頼む */
  private request() {
    const signal = this.gen.signal;
    for (let i = this.playing; i < this.items.length && i <= this.playing + LOOKAHEAD; i++) {
      const item = this.items[i] as Item;
      if (item.audio) continue;
      const seq = i;
      const p = this.chain.then(() => this.synth(item.text, signal, seq));
      item.audio = p;
      this.chain = p;
    }
  }

  private async synth(text: string, signal: AbortSignal, seq: number): Promise<Blob | null> {
    if (signal.aborted || this.degraded || !this.o.source) return null;
    try {
      const readings = readingsFor(this.readings, text);
      const blob = await this.o.source.synth(text, signal, this.replyTraceId, {
        seq,
        ...(readings ? { readings } : {}),
      });
      this.announce({ kind: "jarvis", source: this.o.source.name });
      return blob;
    } catch (e) {
      if (signal.aborted) return null;
      // この返事の残りは標準の声で読む（1 文ごとに待たされないように）
      this.degraded =
        e instanceof SpeechUnavailable
          ? { kind: "fallback", code: e.code, ...(e.reason ? { reason: e.reason } : {}) }
          : { kind: "fallback", code: "network", reason: String((e as Error).message ?? e) };
      return null;
    }
  }

  private announce(s: VoiceState) {
    if (this.announced === s.kind) return;
    this.announced = s.kind;
    this.o.onVoice?.(s);
  }

  private async loop(): Promise<void> {
    const signal = this.ac.signal;
    while (!signal.aborted) {
      const item = this.items[this.playing];
      if (!item) {
        if (this.finished) return;
        await new Promise<void>((r) => {
          this.wake = r;
        });
        this.wake = null;
        continue;
      }
      if (!item.audio) this.request();
      // 待っている間・鳴らしている間に取り消されたら、その文は数えずに読み直しへ
      const id = this.genId;
      const g = this.gen.signal;
      const audio = await item.audio;
      if (signal.aborted) return;
      if (id !== this.genId) continue;
      try {
        this.sounding = true;
        if (audio) await this.o.out.playAudio(audio, g);
        else {
          if (this.degraded) this.announce(this.degraded);
          await this.o.out.speakFallback(item.text, g);
        }
      } catch (e) {
        if (signal.aborted) return;
        if (id === this.genId) this.o.onError?.(e);
      } finally {
        this.sounding = false;
      }
      if (id !== this.genId) continue;
      this.playing++;
      this.request();
    }
  }
}

/** Gateway（POST /v1/speech）経由で MacBook の合成エンジンに作らせる */
export class GatewayVoice implements VoiceSource {
  readonly name = "gateway";
  constructor(
    private readonly fetchImpl: typeof fetch = (...a) => fetch(...a),
    private readonly base = GATEWAY,
    private readonly token: () => string | null = () => store.token,
    private readonly timeoutMs = 12_000,
  ) {}

  async synth(
    text: string,
    signal: AbortSignal,
    replyTraceId: string | null,
    extra: SynthExtra = {},
  ): Promise<Blob> {
    const timeout = AbortSignal.timeout(this.timeoutMs);
    const both = AbortSignal.any ? AbortSignal.any([signal, timeout]) : signal;
    const res = await this.fetchImpl(`${this.base}/v1/speech`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.token() ?? ""}`,
      },
      body: JSON.stringify({
        text,
        ...(replyTraceId ? { reply_trace_id: replyTraceId } : {}),
        // この文に出てくる語の読み（中継が合成の手前で当てる）と、返事の何文目か（最初の音の計測。ADR-0041）
        ...(extra.readings ? { readings: extra.readings } : {}),
        ...(extra.seq !== undefined ? { seq: extra.seq } : {}),
      }),
      signal: both,
    }).catch((e: unknown) => {
      if (signal.aborted) throw e;
      throw new SpeechUnavailable("network", String((e as Error).message ?? e));
    });
    if (!res.ok) {
      const j = (await res.json().catch(() => ({}))) as { error?: string; reason?: string };
      throw new SpeechUnavailable(j.error ?? `http_${res.status}`, j.reason);
    }
    const type = res.headers.get("Content-Type") ?? "";
    if (!type.startsWith("audio/")) throw new SpeechUnavailable("relay_bad_response", type);
    return res.blob();
  }
}

/** 画面に出す、声が変わった理由 */
export function describeVoice(s: VoiceState): string {
  if (s.kind === "jarvis") return "声: Jarvis（MacBook）";
  const why: Record<string, string> = {
    speech_not_configured: "中継が未設定",
    relay_unreachable: "MacBook に届きません（スリープ・オフライン）",
    relay_rejected: "中継が要求を拒みました（秘密の食い違い）",
    engine_unavailable: "MacBook の合成エンジンが止まっています",
    relay_bad_response: "中継の応答が音声ではありません",
    not_allowed: "この端末では使えません",
    unauthorized: "ログインが必要です",
    network: "通信できません",
  };
  return `声: この端末の標準の声（${why[s.code] ?? s.code}）`;
}

// ---------- ブラウザ ----------

const SPEAK_KEY = "jarvis.speak";

/** 読み上げの入・切（端末ごと。既定は切） */
let speakMemo: boolean | null = null;
export const speechPreference = {
  get on(): boolean {
    if (speakMemo !== null) return speakMemo;
    try {
      speakMemo = localStorage.getItem(SPEAK_KEY) === "1";
    } catch {
      speakMemo = false;
    }
    return speakMemo;
  },
  set on(v: boolean) {
    speakMemo = v;
    try {
      localStorage.setItem(SPEAK_KEY, v ? "1" : "0");
    } catch {
      /* private mode: この画面の間だけ */
    }
  },
};

/** 0.05 秒の無音の WAV（iPhone で、利用者の操作の中で 1 回鳴らして音を出せる状態にするため） */
function silentWav(): string {
  const n = 1200;
  const buf = new ArrayBuffer(44 + n * 2);
  const v = new DataView(buf);
  const w = (o: number, s: string) => {
    for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i));
  };
  w(0, "RIFF");
  v.setUint32(4, 36 + n * 2, true);
  w(8, "WAVE");
  w(12, "fmt ");
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true);
  v.setUint16(22, 1, true);
  v.setUint32(24, 24000, true);
  v.setUint32(28, 48000, true);
  v.setUint16(32, 2, true);
  v.setUint16(34, 16, true);
  w(36, "data");
  v.setUint32(40, n * 2, true);
  return URL.createObjectURL(new Blob([buf], { type: "audio/wav" }));
}

/**
 * ブラウザで音を出す。<audio> を 1 つだけ使い回す。
 * iPhone（Safari・ホーム画面の PWA）は、利用者の操作（タップ・送信）の中で一度鳴らした <audio> でないと、後から play() できない。
 * そこで unlock() を「読み上げを入れる」ボタンと「送信」の中で同期的に呼び、無音を鳴らしておく。
 * 標準の声（speechSynthesis）も同じく、操作の中で 1 回 speak しておく。
 */
/**
 * iOS 17 以降の音の扱い（navigator.audioSession）。playback = 消音スイッチが入っていても再生として鳴らす /
 * play-and-record = 録音と再生の両方（マイクを使う間）。無ければ何もしない
 */
export function setAudioSession(type: "playback" | "play-and-record"): void {
  try {
    const session = (navigator as Navigator & { audioSession?: { type: string } }).audioSession;
    if (session && session.type !== type) session.type = type;
  } catch {
    /* 未対応 */
  }
}

export class BrowserOutput implements SpeechOutput {
  private readonly audio: HTMLAudioElement;
  private silence: string | null = null;

  constructor() {
    this.audio = new Audio();
    this.audio.preload = "auto";
    this.audio.setAttribute("playsinline", "");
  }

  /**
   * 利用者の操作の中で呼ぶ（await の前に）。record: true はマイクを使うとき（録音と再生の両方）。
   * 2026-10-11: いつも playback にしていたので、直後のマイクの録音が iPhone で InvalidStateError で断られた
   */
  unlock(opts: { record?: boolean } = {}) {
    setAudioSession(opts.record ? "play-and-record" : "playback");
    // マイクを使うときは無音を鳴らさない（録音を始める前に再生の流れを作ると、iPhone が録音を断ることがある）
    if (opts.record) return;
    this.silence ??= silentWav();
    this.audio.src = this.silence;
    void this.audio.play().catch(() => undefined);
    if ("speechSynthesis" in window) {
      const u = new SpeechSynthesisUtterance(" ");
      u.volume = 0;
      window.speechSynthesis.speak(u);
    }
  }

  playAudio(blob: Blob, signal: AbortSignal): Promise<void> {
    const url = URL.createObjectURL(blob);
    const a = this.audio;
    return new Promise<void>((resolve, reject) => {
      const done = (err?: unknown) => {
        a.onended = null;
        a.onerror = null;
        signal.removeEventListener("abort", onAbort);
        URL.revokeObjectURL(url);
        err ? reject(err) : resolve();
      };
      const onAbort = () => {
        a.pause();
        done();
      };
      signal.addEventListener("abort", onAbort, { once: true });
      a.onended = () => done();
      a.onerror = () => done(new Error("audio element error"));
      a.src = url;
      a.play().catch((e: unknown) => done(e));
    });
  }

  speakFallback(text: string, signal: AbortSignal): Promise<void> {
    if (!("speechSynthesis" in window)) return Promise.resolve();
    const synth = window.speechSynthesis;
    return new Promise<void>((resolve) => {
      const u = new SpeechSynthesisUtterance(text);
      u.lang = "ja-JP";
      const ja = synth.getVoices().find((v) => v.lang.startsWith("ja"));
      if (ja) u.voice = ja;
      // iOS では onend が来ないことがあるので、長さから見た上限で先に進む
      const guard = setTimeout(finish, 3000 + text.length * 400);
      function finish() {
        clearTimeout(guard);
        signal.removeEventListener("abort", onAbort);
        resolve();
      }
      const onAbort = () => {
        synth.cancel();
        finish();
      };
      signal.addEventListener("abort", onAbort, { once: true });
      u.onend = finish;
      u.onerror = finish;
      synth.speak(u);
    });
  }

  stopAll() {
    this.audio.pause();
    if ("speechSynthesis" in window) window.speechSynthesis.cancel();
  }

  /** 今鳴らしている音のどこまでか（0〜1）。標準の声（speechSynthesis）では分からないので null */
  progress(): number | null {
    const a = this.audio;
    if (a.paused || !Number.isFinite(a.duration) || a.duration <= 0) return null;
    return Math.min(1, a.currentTime / a.duration);
  }
}
